import { useEffect, useState } from "react";
import PropTypes from "prop-types";

import { log, t } from "../../../services";
import { strCode, countries } from "../../../consts";
import { objectUtil } from "../../../utils";

import {
  FlexContainers,
  Input,
  DropdownInput,
  SmallButton,
  WrapperContainer
} from "../../../components";
import { FormTextButton } from "./styled";

const MarketsModalForm = ({ data, onFormClose, onFormSave, onRemoveMarket }) => {
  const [selectedCountryId, setSelectedCountryId] = useState(null);
  const [selectedMarketCode, setSelectedMarketCode] = useState("");
  const [selectedMarketName, setSelectedMarketName] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  const getMarketData = () => {
    return {
      market: selectedMarketCode,
      country: countries.find((country) => country.id === selectedCountryId)?.value,
      name: selectedMarketName
    };
  };

  const getOriginalMarketData = () => {
    return {
      market: data.market,
      country: data.country,
      name: data.name
    };
  };

  const onCountrySelect = (id) => {
    const foundedCountry = countries?.find((lang) => lang.id === id);

    if (foundedCountry) {
      setSelectedCountryId(id);
      setSelectedMarketCode(foundedCountry.id);
      setSelectedMarketName(`${foundedCountry?.id} - ${foundedCountry?.value}`);
    }
  };

  const resetForm = () => {
    setSelectedCountryId(null);
    setSelectedMarketCode("");
    setSelectedMarketName("");
  };

  const onSubmit = async () => {
    setIsLoading(true);
    const market = getMarketData();

    try {
      await onFormSave(market);
      resetForm();
    } catch (err) {
      log.error(`An error occurred while saving market data.`, err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (data) {
      setSelectedMarketCode(data.market);
      setSelectedCountryId(countries.find((country) => country.value === data.country)?.id);
      setSelectedMarketName(data.name);
    } else {
      resetForm();
    }
  }, [data]);

  useEffect(() => {
    let isValid = !!selectedMarketCode && !!selectedMarketName;

    const isCountryExist = countries.find((country) => country.id === selectedCountryId);

    if (!isCountryExist) {
      isValid = false;
    }

    if (data) {
      const originalData = getOriginalMarketData();
      const newData = getMarketData();

      if (objectUtil.objEquals(originalData, newData)) {
        isValid = false;
      }
    }

    setIsFormValid(isValid);
  }, [selectedCountryId, selectedMarketCode, selectedMarketName]);

  return (
    <WrapperContainer Container={FlexContainers.FlexColumn} mt={20} minWidth={549}>
      <>
        <WrapperContainer Container={FlexContainers.FlexRow} gap={16} mb={16}>
          <WrapperContainer width={400}>
            <DropdownInput
              id="country-selector"
              title={t(strCode.COUNTRY_TITLE)}
              placeholder={t(strCode.COUNTRY_TITLE)}
              options={countries}
              selectedOption={countries.find((country) => country.id === selectedCountryId)}
              onSelectOption={onCountrySelect}
              disabled={!!data || isLoading}
              isSearchable
            />
          </WrapperContainer>

          <WrapperContainer width={133}>
            <Input
              id="market-code-selector"
              title={t(strCode.MARKET_CODE)}
              placeholder={t(strCode.CODE_PLACEHOLDER)}
              value={selectedMarketCode}
              setValue={setSelectedMarketCode}
              disabled={!!data || isLoading}
            />
          </WrapperContainer>
        </WrapperContainer>

        <WrapperContainer width={549} mb={24}>
          <Input
            id="market-name-selector"
            title={t(strCode.MARKET_NAME)}
            placeholder={t(strCode.MARKET_NAME)}
            value={selectedMarketName}
            setValue={setSelectedMarketName}
            disabled={isLoading}
          />
        </WrapperContainer>

        <WrapperContainer Container={FlexContainers.FlexRowBetween} width="100%">
          {data && (
            <FormTextButton secondary red onClick={onRemoveMarket}>
              {t(strCode.REMOVE_BTN)}
            </FormTextButton>
          )}

          <WrapperContainer Container={FlexContainers.FlexRowEnd} gap={8} width="100%">
            <SmallButton secondary onClick={onFormClose} disabled={isLoading}>
              {t(strCode.CANCEL_BTN)}
            </SmallButton>

            <WrapperContainer width={136}>
              <SmallButton
                disabled={!isFormValid | isLoading}
                loading={isLoading}
                onClick={onSubmit}
                fullWidth>
                {data ? t(strCode.SAVE_BTN) : t(strCode.CREATE_BTN)}
              </SmallButton>
            </WrapperContainer>
          </WrapperContainer>
        </WrapperContainer>
      </>
    </WrapperContainer>
  );
};

MarketsModalForm.propTypes = {
  data: PropTypes.shape({
    country: PropTypes.string,
    market: PropTypes.string,
    name: PropTypes.string
  }),
  onFormClose: PropTypes.func.isRequired,
  onFormSave: PropTypes.func.isRequired,
  onRemoveMarket: PropTypes.func.isRequired
};

export default MarketsModalForm;
